import React, { useCallback, useMemo, useState } from "react";
import UserInfoField from "./UserInfoField";
import { ErrorMessage } from "./ErrorMessage";

// Email validation regex - RFC 5322 compliant (simplified)
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Validation rules
const validateField = (fieldName, value) => {
  const trimmedValue = value?.trim() || "";

  switch (fieldName) {
    case "firstName":
      if (!trimmedValue) {
        return "First name is required";
      }
      if (trimmedValue.length < 2) {
        return "First name must be at least 2 characters";
      }
      if (!/^[a-zA-Z\s'-]+$/.test(trimmedValue)) {
        return "First name can only contain letters, spaces, hyphens, and apostrophes";
      }
      return null;

    case "lastName":
      if (!trimmedValue) {
        return "Last name is required";
      }
      if (trimmedValue.length < 2) {
        return "Last name must be at least 2 characters";
      }
      if (!/^[a-zA-Z\s'-]+$/.test(trimmedValue)) {
        return "Last name can only contain letters, spaces, hyphens, and apostrophes";
      }
      return null;

    case "email":
      if (!trimmedValue) {
        return "Email address is required";
      }
      if (!EMAIL_REGEX.test(trimmedValue)) {
        return "Please enter a valid email address";
      }
      return null;

    default:
      return null;
  }
};

function UserInfoForm({
  userInfo,
  setUserInfo,
  errorMessage,
  uploadError,
  handleS3Upload,
  isPurchaseDisabled,
}) {
  // Track which fields have been touched (blurred)
  const [touched, setTouched] = useState({
    firstName: false,
    lastName: false,
    email: false,
  });

  // Compute validation errors
  const errors = useMemo(() => ({
    firstName: validateField("firstName", userInfo.firstName),
    lastName: validateField("lastName", userInfo.lastName),
    email: validateField("email", userInfo.email),
  }), [userInfo]);

  // Check if form is valid (no errors)
  const isFormValid = useMemo(
    () => !errors.firstName && !errors.lastName && !errors.email,
    [errors]
  );

  // Check if all fields are filled (for the general message)
  const allFieldsFilled = useMemo(
    () => userInfo.firstName && userInfo.lastName && userInfo.email,
    [userInfo]
  );

  // Single stable onChange for user info
  const handleUserChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      setUserInfo((prev) => ({ ...prev, [name]: value }));
    },
    [setUserInfo]
  );

  // Handle blur to mark field as touched
  const handleBlur = useCallback((e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
  }, []);

  // Check if any field has been touched and has an error
  const hasVisibleErrors = useMemo(
    () =>
      (touched.firstName && errors.firstName) ||
      (touched.lastName && errors.lastName) ||
      (touched.email && errors.email),
    [touched, errors]
  );

  return (
    <div className="flex flex-col w-full mt-10">
      <h4 className="text-xl font-semibold text-left mb-4 text-primary">
        Step 3: Enter Your Information
      </h4>

      <UserInfoField
        label="First Name"
        fieldName="firstName"
        value={userInfo.firstName}
        onChange={handleUserChange}
        onBlur={handleBlur}
        required
        error={errors.firstName}
        touched={touched.firstName}
      />

      <UserInfoField
        label="Last Name"
        fieldName="lastName"
        value={userInfo.lastName}
        onChange={handleUserChange}
        onBlur={handleBlur}
        required
        error={errors.lastName}
        touched={touched.lastName}
      />

      <UserInfoField
        label="Email"
        fieldName="email"
        type="email"
        value={userInfo.email}
        onChange={handleUserChange}
        onBlur={handleBlur}
        required
        error={errors.email}
        touched={touched.email}
      />

      {/* Show general required message only if no specific errors are visible yet */}
      {!allFieldsFilled && !hasVisibleErrors && (
        <p className="text-amber-600 text-sm mt-2 flex items-center gap-1.5">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          All fields are required to proceed
        </p>
      )}

      {/* Show success message when form is valid */}
      {isFormValid && allFieldsFilled && (
        <p className="text-emerald-600 text-sm mt-2 flex items-center gap-1.5">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          All information looks good!
        </p>
      )}

      {(errorMessage || uploadError) && (
        <ErrorMessage message={errorMessage || uploadError} />
      )}

      {/* Checkout component moved to Main.jsx */}
    </div>
  );
}

export default UserInfoForm;
